import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

export function TwoFactorSetupPage() {
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const { refresh, me } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (me?.totpEnabled) { nav('/'); return; }
    void api.post<{ qrCode: string; secret: string }>('/auth/2fa/setup')
      .then((r) => { setQr(r.qrCode); setSecret(r.secret); })
      .catch((e) => setErr(e.message ?? 'Fehler'));
  }, [me, nav]);

  async function activate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await api.post<{ backupCodes: string[] }>('/auth/2fa/activate', { code });
      setBackupCodes(r.backupCodes);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Fehler');
    } finally { setBusy(false); }
  }

  if (backupCodes.length > 0) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
        <div className="card w-full max-w-lg">
          <h1 className="text-xl font-bold text-rla-700 mb-2">2FA aktiviert</h1>
          <p className="text-sm text-slate-700 mb-4">
            Backup-Codes sicher speichern (Bitwarden, Tresor). Jeder Code kann <strong>einmal</strong> genutzt werden.
          </p>
          <div className="bg-slate-100 rounded-md p-4 grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c) => (
              <div key={c} className="bg-white rounded px-2 py-1 text-center border">{c}</div>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-md p-3 mt-4">
            Diese Codes werden <strong>nur jetzt</strong> angezeigt!
          </div>
          <button onClick={() => nav('/')} className="btn-primary w-full mt-4">Habe ich gesichert — weiter</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
      <div className="card w-full max-w-lg">
        <h1 className="text-xl font-bold text-rla-700 mb-2">Zwei-Faktor einrichten</h1>
        <p className="text-sm text-slate-700 mb-4">QR-Code mit Authenticator-App scannen, dann den 6-stelligen Code eingeben.</p>
        {qr && (
          <div className="flex flex-col items-center gap-3 mb-4">
            <img src={qr} alt="2FA QR" className="border border-slate-200 rounded-md" />
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer">Manuell eingeben</summary>
              <code className="font-mono break-all mt-2 block">{secret}</code>
            </details>
          </div>
        )}
        <form onSubmit={activate} className="space-y-3">
          <input type="text" inputMode="numeric" className="input text-center text-2xl tracking-widest font-mono" value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))} maxLength={6} placeholder="000000" required />
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? 'Aktiviere …' : 'Aktivieren'}
          </button>
        </form>
      </div>
    </div>
  );
}
